/**
 * GuideDrawerLayout — B-2026-0088 Phase A1
 *
 * Extracts the three responsive layout modes that were previously duplicated
 * in EvalResultsGuideOverlay and StudioGuideOverlay:
 *   - DesktopDrawer   — fixed 320px right drawer with retract/expand handle
 *   - MobilePortraitSheet — bottom pull-up sheet (~50vh)
 *   - MobileLandscapePill — collapsed pill → half-height sheet
 *
 * Both overlays now render `<GuideDrawerLayout>` and pass their content as
 * `children`, eliminating the code duplication.
 *
 * Props:
 *   isOpen       — whether to render at all
 *   onDismiss    — called when the user closes the guide
 *   heading      — card header text
 *   contextHint  — small footer hint text
 *   children     — the rendered card body (GuideCardContent)
 *   cardRef      — forwarded ref to the card panel element (Phase A3 arrow source)
 */

import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  QuestionMarkCircleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import Portal from '@/components/ui/Portal';
import { useAdaptiveGuide } from '@/contexts/AdaptiveGuideContext';
import { useResponsiveMode } from '@/hooks/useResponsiveMode';

const DRAWER_WIDTH = 320;

// ---------------------------------------------------------------------------
// Shared props
// ---------------------------------------------------------------------------

export interface GuideDrawerLayoutProps {
  isOpen: boolean;
  onDismiss: () => void;
  heading: string;
  contextHint: string;
  children: React.ReactNode;
  /** Forwarded ref to the card panel div (used by AnchorHighlight in Phase A3). */
  cardRef?: React.RefObject<HTMLDivElement | null>;
}

// ---------------------------------------------------------------------------
// Inner card shell (shared across all three layouts)
// ---------------------------------------------------------------------------

interface InnerCardProps {
  heading: string;
  contextHint: string;
  onDismiss: () => void;
  children: React.ReactNode;
  transparent?: boolean;
  className?: string;
  cardRef?: React.RefObject<HTMLDivElement | null>;
}

function InnerCard({ heading, contextHint, onDismiss, children, transparent = false, className = '', cardRef }: InnerCardProps) {
  return (
    <div
      ref={cardRef}
      className={`pointer-events-auto flex flex-col overflow-hidden ${className}`}
      style={transparent ? { border: 'none', borderRadius: 0 } : {
        backgroundColor: 'color-mix(in srgb, var(--color-surface-raised-2) 98%, transparent)',
        border: '1px solid color-mix(in srgb, var(--color-accent-cyan) 20%, var(--color-border-subtle))',
        borderRadius: '12px',
      }}
      role="complementary"
      aria-label="Adaptive Guide"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0"
        style={{ borderColor: 'var(--color-border-subtle)' }}
      >
        <div className="flex items-center gap-2">
          <QuestionMarkCircleIcon
            className="h-4 w-4 flex-shrink-0"
            style={{ color: 'var(--color-accent-cyan)' }}
            aria-hidden="true"
          />
          <span
            className="text-sm font-semibold"
            style={{ color: 'var(--color-text-primary)', fontFamily: 'var(--font-display)' }}
          >
            {heading}
          </span>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss guide"
          className="rounded-md p-1 transition-colors"
          style={{ color: 'var(--color-text-muted)' }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-primary)';
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-surface-raised-3)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-muted)';
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
          }}
        >
          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {children}
      </div>

      {/* Context hint footer */}
      <div
        className="px-4 py-2 border-t flex-shrink-0"
        style={{ borderColor: 'var(--color-border-subtle)' }}
      >
        <p
          className="text-xs"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {contextHint}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Desktop flush drawer (≥1024px)
// ---------------------------------------------------------------------------

function DesktopDrawer({ isOpen, onDismiss, heading, contextHint, children, cardRef }: GuideDrawerLayoutProps) {
  const { isDrawerCollapsed, setDrawerCollapsed } = useAdaptiveGuide();

  if (!isOpen) return null;

  const collapsed = isDrawerCollapsed;

  return (
    <Portal zIndex="z-[10000]">
      {/* Dimming scrim — only when drawer is expanded */}
      {!collapsed && (
        <div
          className="pointer-events-none fixed inset-0"
          style={{
            backgroundColor: 'rgba(0,0,0,0.18)',
            transition: 'opacity 300ms ease',
          }}
          aria-hidden="true"
        />
      )}

      {/* Drawer + retract handle wrapper */}
      <div className="pointer-events-none fixed inset-0" aria-hidden={!isOpen}>
        {/* Retract/expand handle */}
        <button
          type="button"
          onClick={() => setDrawerCollapsed(!collapsed)}
          aria-label={collapsed ? 'Expand guide drawer' : 'Retract guide drawer'}
          data-testid="drawer-toggle-handle"
          className="pointer-events-auto absolute flex items-center justify-center"
          style={{
            top: '50%',
            right: collapsed ? 0 : `${DRAWER_WIDTH}px`,
            transform: 'translateY(-50%)',
            width: '20px',
            height: '64px',
            backgroundColor: 'color-mix(in srgb, var(--color-surface-raised-2) 98%, transparent)',
            border: '1px solid color-mix(in srgb, var(--color-accent-cyan) 20%, var(--color-border-subtle))',
            borderRight: collapsed ? '1px solid color-mix(in srgb, var(--color-accent-cyan) 20%, var(--color-border-subtle))' : 'none',
            borderRadius: '8px 0 0 8px',
            cursor: 'pointer',
            color: 'var(--color-text-muted)',
            transition: 'right 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 1,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-accent-cyan)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-muted)';
          }}
        >
          {collapsed
            ? <ChevronLeftIcon className="h-3 w-3" aria-hidden="true" />
            : <ChevronRightIcon className="h-3 w-3" aria-hidden="true" />}
        </button>

        {/* Drawer panel */}
        <div
          className="pointer-events-auto absolute top-0 bottom-0 right-0 flex flex-col"
          style={{
            width: `${DRAWER_WIDTH}px`,
            transform: collapsed ? `translateX(${DRAWER_WIDTH}px)` : 'translateX(0)',
            transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            backgroundColor: 'color-mix(in srgb, var(--color-surface-raised-2) 98%, transparent)',
            borderLeft: '1px solid color-mix(in srgb, var(--color-accent-cyan) 20%, var(--color-border-subtle))',
          }}
          aria-hidden={collapsed}
        >
          <InnerCard
            heading={heading}
            contextHint={contextHint}
            onDismiss={onDismiss}
            transparent={true}
            className="flex-1"
            cardRef={cardRef}
          >
            {children}
          </InnerCard>
        </div>
      </div>
    </Portal>
  );
}

// ---------------------------------------------------------------------------
// Mobile portrait — bottom pull-up sheet
// ---------------------------------------------------------------------------

function MobilePortraitSheet({ isOpen, onDismiss, heading, contextHint, children, cardRef }: GuideDrawerLayoutProps) {
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (isOpen) setExpanded(true);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Portal zIndex="z-[10000]">
      <div className="pointer-events-none fixed inset-0" aria-hidden={!isOpen}>
        <div
          className="pointer-events-auto absolute bottom-0 left-0 right-0 rounded-t-2xl"
          style={{
            height: expanded ? '50vh' : '3rem',
            transition: 'height 250ms cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
            backgroundColor: 'color-mix(in srgb, var(--color-surface-raised-2) 98%, transparent)',
            border: '1px solid color-mix(in srgb, white 10%, transparent)',
            borderBottom: 'none',
          }}
        >
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="w-full flex flex-col items-center py-2 gap-1"
            aria-label={expanded ? 'Collapse guide' : 'Expand guide'}
          >
            <div
              className="w-10 h-1 rounded-full"
              style={{ backgroundColor: 'var(--color-border-strong)' }}
            />
            <div className="flex items-center gap-1.5">
              <QuestionMarkCircleIcon
                className="h-3.5 w-3.5"
                style={{ color: 'var(--color-accent-cyan)' }}
                aria-hidden="true"
              />
              <span
                className="text-xs font-semibold"
                style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-display)' }}
              >
                Guide
              </span>
              {expanded
                ? <ChevronDownIcon className="h-3 w-3" style={{ color: 'var(--color-text-muted)' }} aria-hidden="true" />
                : <ChevronUpIcon className="h-3 w-3" style={{ color: 'var(--color-text-muted)' }} aria-hidden="true" />
              }
            </div>
          </button>

          {expanded && (
            <div className="flex flex-col h-[calc(50vh-3.5rem)] overflow-hidden">
              <InnerCard
                heading={heading}
                contextHint={contextHint}
                onDismiss={onDismiss}
                className="flex-1"
                cardRef={cardRef}
              >
                {children}
              </InnerCard>
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}

// ---------------------------------------------------------------------------
// Mobile landscape — collapsed pill → half-height sheet
// ---------------------------------------------------------------------------

function MobileLandscapePill({ isOpen, onDismiss, heading, contextHint, children, cardRef }: GuideDrawerLayoutProps) {
  const [pillExpanded, setPillExpanded] = useState(false);

  useEffect(() => {
    if (!isOpen) setPillExpanded(false);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Portal zIndex="z-[10000]">
      <div className="pointer-events-none fixed inset-0" aria-hidden={!isOpen}>
        {pillExpanded ? (
          <div
            className="pointer-events-auto absolute bottom-0 left-0 right-0 rounded-t-xl"
            style={{
              height: '50vh',
              overflow: 'hidden',
              backgroundColor: 'color-mix(in srgb, var(--color-surface-raised-2) 98%, transparent)',
              border: '1px solid color-mix(in srgb, white 10%, transparent)',
              borderBottom: 'none',
            }}
          >
            <div
              className="flex items-center justify-between px-4 py-2 border-b"
              style={{ borderColor: 'var(--color-border-subtle)' }}
            >
              <div className="flex items-center gap-2">
                <QuestionMarkCircleIcon
                  className="h-4 w-4"
                  style={{ color: 'var(--color-accent-cyan)' }}
                  aria-hidden="true"
                />
                <span
                  className="text-xs font-semibold"
                  style={{ color: 'var(--color-text-primary)', fontFamily: 'var(--font-display)' }}
                >
                  Guide
                </span>
              </div>
              <button
                type="button"
                onClick={() => setPillExpanded(false)}
                aria-label="Collapse guide"
                className="p-1 rounded"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <div className="h-[calc(50vh-2.5rem)] overflow-y-auto px-4 py-3" ref={cardRef as React.RefObject<HTMLDivElement>}>
              <InnerCard
                heading={heading}
                contextHint={contextHint}
                onDismiss={onDismiss}
                className="border-0 shadow-none"
              >
                {children}
              </InnerCard>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setPillExpanded(true)}
            className="pointer-events-auto absolute bottom-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-lg"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--color-surface-raised-3) 90%, transparent)',
              border: '1px solid color-mix(in srgb, var(--color-accent-cyan) 40%, transparent)',
              backdropFilter: 'blur(8px)',
              color: 'var(--color-accent-cyan)',
            }}
            aria-label="Open guide"
          >
            <QuestionMarkCircleIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span className="text-xs font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
              Guide
            </span>
          </button>
        )}
      </div>
    </Portal>
  );
}

// ---------------------------------------------------------------------------
// Main export — picks the correct layout based on responsive mode
// ---------------------------------------------------------------------------

export function GuideDrawerLayout(props: GuideDrawerLayoutProps) {
  const { isDesktop, isLandscape } = useResponsiveMode();

  if (isDesktop) {
    return <DesktopDrawer {...props} />;
  }
  if (isLandscape) {
    return <MobileLandscapePill {...props} />;
  }
  return <MobilePortraitSheet {...props} />;
}

export default GuideDrawerLayout;
